import { useEffect, useMemo } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { WithSkeleton, WithSkeletonContext } from '@r1-frontend/ui-react/components/loaders/Skeleton';
import SlideCarousel from '@r1-frontend/ui-react/components/SlideCarousel';
import { TicketDiscount, WalletAdd } from '@r1-frontend/ui-react/components/svg/money';
import ToggleCard from '@r1-frontend/ui-react/organisms/ToggleCard';
import { TInfoBlockData } from '@r1-frontend/ui-react/organisms/ToggleCard/types';
import { BorderRadius } from '@r1-frontend/ui-react/tokens/borderRadius';
import { isSomeEnum } from '@r1-frontend/shared/guards/isSomeEnum';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import { EPaymentTypeIds } from '~/src/features/payments-abn/store/actionCreators';
import { updatePaymentTypeId } from '~/src/features/payments-abn/store/actions';
import { TState } from '~/src/store';

import { ActionSectionWrapper } from '~/src/features/payments-abn/styled';
import * as ST from './styled';

const types: TInfoBlockData[] = [
    {
        id: EPaymentTypeIds.DEFAULT,
        title: 'Просто оплатить',
        info: 'Единоразово пополнить баланс на любую сумму',
        icon: <WalletAdd size={36}/>,
    },
    {
        id: EPaymentTypeIds.WHOLESALE,
        title: 'Оформить абонемент',
        info: 'И экономить каждый месяц до 15%',
        icon: <TicketDiscount size={36}/>,
    },
];

const PaymentType = (): JSX.Element | null => {
    const { query } = useRouter();
    const dispatch = useDispatch();
    const { paymentTypeId, wholesaleCheaperData, frameUrl } = useSelector((state: TState) => ({
        paymentTypeId: state.payment.paymentTypeId,
        wholesaleCheaperData: state.wholesaleCheaperData,
        frameUrl: state.payment.result.frameUrl,
    }), shallowEqual);
    const { isLoading } = wholesaleCheaperData;

    const isMobile = useMatchMediaByWidth(wideBreakpoints.mobile);

    const onClickHandler = (id: TInfoBlockData['id']) => {
        if (id !== paymentTypeId) {
            dispatch(updatePaymentTypeId(id as EPaymentTypeIds));
        }
    };

    useEffect(() => {
        const { paymentType } = query;
        const isPaymentTypeId = isSomeEnum(EPaymentTypeIds);

        if (isPaymentTypeId(paymentType) && paymentType !== paymentTypeId) {
            dispatch(updatePaymentTypeId(paymentType));
        }
    }, []);

    const renderTypes = () => (
        types.map(type => (
            <SkeletonTheme
                key={type.id}
                borderRadius={isMobile ? BorderRadius.componentRadius : BorderRadius.OuterBlockRadius}
            >
                <WithSkeleton wrapper={ST.TypeWrapper}>
                    <ST.TypeWrapper>
                        <ToggleCard
                            isActive={type.id === paymentTypeId}
                            type={type}
                            onToggleType={onClickHandler}
                        />
                    </ST.TypeWrapper>
                </WithSkeleton>
            </SkeletonTheme>
        ))
    );

    const slideIndex = useMemo(() => types.findIndex(type => type.id === paymentTypeId), [paymentTypeId]);

    if (frameUrl) {
        return null;
    }

    return (
        <WithSkeletonContext isLoading={isLoading}>
            <ActionSectionWrapper>
                {isMobile
                    ? <SlideCarousel
                        withoutArrows={true}
                        withoutDots={true}
                        slideIndex={slideIndex}
                        slideWidth={190}
                        cellSpacing={isLoading ? 8 : 0}
                    >
                        {renderTypes()}
                    </SlideCarousel>
                    : <ST.TypeContent isLoading={isLoading}>
                        {renderTypes()}
                    </ST.TypeContent>
                }
            </ActionSectionWrapper>
        </WithSkeletonContext>
    );
};

export default PaymentType;
